import {
  CmsContentAboutGallerySchema,
  type CmsContentAboutGalleryForm,
} from "@/modules/cms/domain/contracts";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { FileUploadItemStatus } from "@/shared/ui/PresignedImageUploadTrigger";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import {
  Button,
  Checkbox,
  FormCheckbox,
  FormInput,
  FormNumber,
  FormRoot,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  CheckCircle2Icon,
  Loader2,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-gallery";

type AboutGalleryFormProps = {
  content: CmsContentAboutGalleryForm | null;
  onSubmit: (content: CmsContentAboutGalleryForm) => void | Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutGalleryForm = {
  title: {},
  previewCount: 8,
  items: [],
  isActive: true,
};

type PendingUploadItem = {
  file: File;
  status: FileUploadItemStatus | "saving";
  key?: string;
  objectUrl: string;
};

function syncItemOrders(
  methods: UseFormReturn<CmsContentAboutGalleryForm>,
) {
  const items = methods.getValues("items") ?? [];
  items.forEach((_, idx) => {
    methods.setValue(`items.${idx}.order`, idx, { shouldDirty: true });
  });
}

function buildGalleryPayload(
  vals: CmsContentAboutGalleryForm,
): CmsContentAboutGalleryForm {
  const items = (vals.items ?? []).map((item, i) => ({
    id: item.id?.trim() || nanoid(),
    imageKey: item.imageKey ?? "",
    blurhash: item.blurhash ?? "",
    isActive: item.isActive ?? true,
    order: i,
  }));
  return { ...vals, items };
}

export function AboutGalleryForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutGalleryFormProps) {
  const methods = RHF.useForm<CmsContentAboutGalleryForm>({
    resolver: zodResolver(
      CmsContentAboutGallerySchema,
    ) as Resolver<CmsContentAboutGalleryForm>,
    defaultValues: content ?? defaultContent,
  });
  const { fields, append, remove, move } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });

  const [pendingUploads, setPendingUploads] = useState<PendingUploadItem[]>([]);
  const pendingUploadsRef = useRef(pendingUploads);
  pendingUploadsRef.current = pendingUploads;

  const [uploadBusy, setUploadBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = methods.watch("items") ?? [];
  const someSelected = selectedIds.size > 0;
  const allSelected =
    items.length > 0 && selectedIds.size === items.length;
  const headerChecked = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;

  useEffect(() => {
    return () => {
      for (const item of pendingUploadsRef.current) {
        URL.revokeObjectURL(item.objectUrl);
      }
    };
  }, []);

  const toggleOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(
          new Set(items.map((item) => item.id).filter(Boolean)),
        );
      } else {
        setSelectedIds(new Set());
      }
    },
    [items],
  );

  const clearPendingAndRevoke = useCallback(() => {
    setPendingUploads((prev) => {
      for (const item of prev) URL.revokeObjectURL(item.objectUrl);
      return [];
    });
  }, []);

  const handleFilesSelected = useCallback((files: File[]) => {
    const next: PendingUploadItem[] = files.map((file) => ({
      file,
      status: "pending" as const,
      objectUrl: URL.createObjectURL(file),
    }));
    setPendingUploads(next);
  }, []);

  const handleFileProgress = useCallback(
    (index: number, status: FileUploadItemStatus, key?: string) => {
      setPendingUploads((prev) => {
        const next = [...prev];
        const item = next[index];
        if (!item) return prev;
        next[index] = { ...item, status, key };
        return next;
      });
    },
    [],
  );

  const saveContent = useCallback(
    async (vals?: CmsContentAboutGalleryForm) => {
      setError(null);
      const payload = buildGalleryPayload(vals ?? methods.getValues());
      await Promise.resolve(onSubmit(payload));
      methods.reset(payload);
      return payload;
    },
    [methods, onSubmit],
  );

  const appendAndAutoSave = useCallback(
    async (keys: string[], blurhashes: string[]) => {
      if (!keys.length || submitting) return;

      setError(null);
      const startOrder = fields.length;
      append(
        keys.map((key, i) => ({
          id: nanoid(),
          imageKey: key,
          blurhash: blurhashes[i] ?? "",
          isActive: true,
          order: startOrder + i,
        })),
      );
      clearPendingAndRevoke();

      try {
        await saveContent();
      } catch (err) {
        setError(err instanceof Error ? err.message : "ບັນທຶກລົ້ມເຫຼວ");
      }
    },
    [append, clearPendingAndRevoke, fields.length, saveContent, submitting],
  );

  const handleUploadSuccess = useCallback(
    (fileKey: string, blurhash?: string | null) => {
      void appendAndAutoSave([fileKey], [blurhash ?? ""]);
    },
    [appendAndAutoSave],
  );

  const handleUploadSuccessMultiple = useCallback(
    (keys: string[], blurhashes: string[]) => {
      void appendAndAutoSave(keys, blurhashes);
    },
    [appendAndAutoSave],
  );

  const handleDelete = useCallback((index: number) => {
    setIndexToDelete(index);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (indexToDelete === null) return;
    const id = methods.getValues(`items.${indexToDelete}.id`);
    remove(indexToDelete);
    syncItemOrders(methods);
    if (id) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    setDeleteDialogOpen(false);
    setIndexToDelete(null);
  }, [indexToDelete, methods, remove]);

  const confirmBulkDelete = useCallback(() => {
    const indices = fields
      .map((field, index) => ({
        id: methods.getValues(`items.${index}.id`) ?? field.id,
        index,
      }))
      .filter(({ id }) => selectedIds.has(id))
      .map(({ index }) => index)
      .sort((a, b) => b - a);

    for (const idx of indices) {
      remove(idx);
    }
    syncItemOrders(methods);
    setSelectedIds(new Set());
    setBulkDeleteOpen(false);
  }, [fields, methods, remove, selectedIds]);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },
    [],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      const dragIndex = Number.parseInt(
        e.dataTransfer.getData("text/plain"),
        10,
      );
      if (Number.isNaN(dragIndex) || dragIndex === dropIndex) return;
      move(dragIndex, dropIndex);
      syncItemOrders(methods);
    },
    [methods, move],
  );

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutGalleryForm) => {
      try {
        await saveContent(vals);
      } catch (err) {
        setError(err instanceof Error ? err.message : "ບັນທຶກລົ້ມເຫຼວ");
      }
    },
    [saveContent],
  );

  const uploadTriggerProps = {
    keyPrefix: IMAGE_KEY_PREFIX,
    onFilesSelected: handleFilesSelected,
    onFileProgress: handleFileProgress,
    onSuccess: handleUploadSuccess,
    onSuccessMultiple: handleUploadSuccessMultiple,
    multiple: true as const,
    disabled: submitting,
    isSaving: submitting,
    onBusyChange: setUploadBusy,
  };

  const renderPendingTile = (item: PendingUploadItem) => (
    <div
      key={item.objectUrl}
      className="relative overflow-hidden rounded-lg border-2 border-muted-foreground/30 border-dashed bg-muted/10"
    >
      <div className="aspect-square bg-muted">
        <img
          src={item.objectUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
        {item.status === "pending" && (
          <span className="text-white text-xs">ກຳລັງຮອງ...</span>
        )}
        {item.status === "uploading" && (
          <Loader2 className="size-8 animate-spin text-white" />
        )}
        {item.status === "done" && (
          <CheckCircle2Icon className="size-8 text-green-400" />
        )}
        {item.status === "saving" && (
          <div className="flex flex-col items-center gap-1">
            <Loader2 className="size-8 animate-spin text-white" />
            <span className="text-white text-xs">ກຳລັງບັນທຶກ...</span>
          </div>
        )}
        {item.status === "error" && (
          <XCircleIcon className="size-8 text-destructive" />
        )}
      </div>
    </div>
  );

  return (
    <>
      <FormRoot<CmsContentAboutGalleryForm>
        methods={methods as UseFormReturn<CmsContentAboutGalleryForm>}
        onSubmit={handleSubmit}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
            <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
            <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
          </div>
          <FormNumber
            name="previewCount"
            label="ຈຳນວນຮູບ preview ຫນ້າ About"
            min={1}
            max={48}
            className="max-w-xs"
          />

          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">ຄັງຮູບຄັງຮູບ</h3>
                <p className="text-muted-foreground text-sm">
                  ອັບໂຫຼດແລ້ວບັນທຶກອັດຕະໂນມັດ · ລາກເພື່ອຈັດລຳດັບ
                </p>
              </div>
              <div className="flex items-center gap-2">
                {someSelected && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteOpen(true)}
                  >
                    <TrashIcon className="size-4" />
                    ລຶບທີ່ເລືອກ ({selectedIds.size})
                  </Button>
                )}
                <PresignedImageUploadTrigger
                  {...uploadTriggerProps}
                  variant="button"
                  label="ອັບໂຫຼດຮູບ"
                />
              </div>
            </div>

            {fields.length === 0 && pendingUploads.length === 0 ? (
              <div className="flex min-h-[200px] items-center justify-center rounded-lg">
                <PresignedImageUploadTrigger
                  {...uploadTriggerProps}
                  variant="tile"
                  label="ເພີ່ມຮູບຄັງຮູບ"
                  className="max-w-[240px]"
                />
              </div>
            ) : fields.length === 0 && pendingUploads.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                {pendingUploads.map(renderPendingTile)}
                <PresignedImageUploadTrigger
                  {...uploadTriggerProps}
                  variant="tile"
                />
              </div>
            ) : (
              <div className="space-y-3">
                {fields.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={headerChecked}
                      onCheckedChange={(val) =>
                        toggleAll(val === true || val === "indeterminate")
                      }
                      aria-label="ເລືອກທັງໝົດ"
                    />
                    <span className="text-muted-foreground text-sm">
                      {someSelected
                        ? `ເລືອກ ${selectedIds.size} ຮູບ`
                        : "ເລືອກທັງໝົດ"}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                  {fields.map((field, index) => {
                    const itemId =
                      methods.watch(`items.${index}.id`) ?? field.id;
                    const imageKey =
                      methods.watch(`items.${index}.imageKey`) ?? "";
                    const src = resolveImageSrc(imageKey);

                    return (
                      // biome-ignore lint/a11y/noStaticElementInteractions: draggable card
                      <div
                        key={field.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`group relative cursor-move overflow-hidden rounded-lg border-2 transition-all hover:shadow-lg ${
                          index === 0
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border"
                        }`}
                      >
                        <input
                          type="hidden"
                          {...methods.register(`items.${index}.id`)}
                        />
                        <input
                          type="hidden"
                          {...methods.register(`items.${index}.imageKey`)}
                        />
                        <input
                          type="hidden"
                          {...methods.register(`items.${index}.blurhash`)}
                        />
                        <input
                          type="hidden"
                          {...methods.register(`items.${index}.order`)}
                        />
                        <input
                          type="hidden"
                          {...methods.register(`items.${index}.isActive`)}
                        />
                        <div className="absolute top-2 left-2 z-10">
                          <Checkbox
                            checked={selectedIds.has(itemId)}
                            onCheckedChange={(val) =>
                              toggleOne(itemId, val === true)
                            }
                            aria-label={`ເລືອກຮູບ ${index + 1}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="aspect-square bg-muted">
                          {src ? (
                            <img
                              src={src}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-muted-foreground text-xs">
                              ບໍ່ມີຮູບ
                            </div>
                          )}
                        </div>
                        {index === 0 && (
                          <div className="absolute top-2 left-10 rounded bg-primary px-2 py-1 font-medium text-primary-foreground text-xs">
                            ຮູບແຮກ
                          </div>
                        )}
                        <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(index)}
                          >
                            <TrashIcon className="size-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {pendingUploads.map(renderPendingTile)}
                  <PresignedImageUploadTrigger
                    {...uploadTriggerProps}
                    variant="tile"
                  />
                </div>
              </div>
            )}

            {error && (
              <p className="mt-3 text-destructive text-sm" role="alert">
                {error}
              </p>
            )}
          </div>

          <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              disabled={submitting || uploadBusy}
            >
              {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              ຍົກເລີກ
            </Button>
          </div>
        </div>
      </FormRoot>

      <Modal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບຮູບ</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຮູບນີ້?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              ລຶບ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">ລຶບຫຼາຍຮູບ</h3>
          <p className="text-muted-foreground text-sm">
            ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ {selectedIds.size} ຮູບທີ່ເລືອກ?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              ລຶບ
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
